<template>
  <!-- bootstrap-vue 2.23 b-pagination'ın kullanılan alt kümesi, aynı BS4 DOM'u ve aria etiketleri
       (smoke `Go to next page` ve `.page-item.active` ile arar). Sayfa listesi, üç nokta ve dar
       ekranda gizlenen düğmeler (bv-d-xs-down-none) bootstrap-vue algoritmasıyla hesaplanır. -->
  <ul ref="ul" role="menubar" aria-disabled="false" aria-label="Pagination" class="pagination b-pagination" @keydown="handleKeyNav">
    <li v-for="button in buttons" :key="button.key" :role="button.role" class="page-item" :class="button.classes" :aria-hidden="button.ariaHidden">
      <span v-if="button.isEllipsis" class="page-link">{{ ellipsisText }}</span>
      <span v-else-if="button.disabled" class="page-link" :aria-label="button.label" aria-disabled="true">{{ button.text }}</span>
      <button v-else type="button" class="page-link" :role="button.itemRole" :tabindex="button.tabindex" :aria-label="button.label"
              :aria-checked="button.ariaChecked" :aria-posinset="button.posinset" :aria-setsize="button.setsize"
              @click="onClick($event, button.page)">{{ button.text }}</button>
    </li>
  </ul>
</template>

<script>
  const ELLIPSIS_THRESHOLD = 3;
  const DEFAULT_LIMIT = 5;
  const HIDE_XS = 'bv-d-xs-down-none';

  const toInt = (value, fallback) => {
    const number = parseInt(value, 10);
    return isNaN(number) ? fallback : number;
  };

  export default {
    name: 'BasePagination',
    emits: ['update:currentPage', 'change'],
    props: {
      currentPage: { type: [Number, String], default: 1 },
      totalRows: { type: [Number, String], default: 0 },
      perPage: { type: [Number, String], default: 20 },
      limit: { type: [Number, String], default: DEFAULT_LIMIT }
    },
    data() {
      return {
        localPage: Math.max(toInt(this.currentPage, 1), 1),
        firstText: '«',
        prevText: '‹',
        nextText: '›',
        lastText: '»',
        ellipsisText: '…'
      };
    },
    computed: {
      numberOfPages() {
        const perPage = Math.max(toInt(this.perPage, 1), 1);
        return Math.max(Math.ceil(Math.max(toInt(this.totalRows, 0), 0) / perPage), 1);
      },
      localLimit() {
        const limit = toInt(this.limit, 1) || 1;
        return limit < 1 ? DEFAULT_LIMIT : limit;
      },
      page() {
        return Math.min(Math.max(this.localPage, 1), this.numberOfPages);
      },
      /* bootstrap-vue paginationParams (firstNumber/lastNumber/hideEllipsis kapalı) */
      params() {
        const limit = this.localLimit;
        const numberOfPages = this.numberOfPages;
        const currentPage = this.page;
        let showFirstDots = false;
        let showLastDots = false;
        let numberOfLinks = limit;
        let startNumber = 1;

        if (numberOfPages <= limit) {
          numberOfLinks = numberOfPages;
        } else if (currentPage < limit - 1 && limit > ELLIPSIS_THRESHOLD) {
          showLastDots = true;
          numberOfLinks = limit - 1;
        } else if (numberOfPages - currentPage + 2 < limit && limit > ELLIPSIS_THRESHOLD) {
          showFirstDots = true;
          numberOfLinks = limit - 1;
          startNumber = numberOfPages - numberOfLinks + 1;
        } else {
          if (limit > ELLIPSIS_THRESHOLD) {
            numberOfLinks = limit - 2;
            showFirstDots = true;
            showLastDots = true;
          }
          startNumber = currentPage - Math.floor(numberOfLinks / 2);
        }
        if (startNumber < 1) {
          startNumber = 1;
          showFirstDots = false;
        } else if (startNumber > numberOfPages - numberOfLinks) {
          startNumber = numberOfPages - numberOfLinks + 1;
          showLastDots = false;
        }
        numberOfLinks = Math.min(numberOfLinks, numberOfPages - startNumber + 1);
        return { showFirstDots, showLastDots, numberOfLinks, startNumber };
      },
      pageList() {
        const { numberOfLinks, startNumber } = this.params;
        const pages = Array.from({ length: numberOfLinks }, (_, i) => ({ number: startNumber + i, classes: null }));
        if (pages.length > 3) {
          const idx = this.page - startNumber;
          if (idx === 0) {
            for (let i = 3; i < pages.length; i++) pages[i].classes = HIDE_XS;
          } else if (idx === pages.length - 1) {
            for (let i = 0; i < pages.length - 3; i++) pages[i].classes = HIDE_XS;
          } else {
            for (let i = 0; i < idx - 1; i++) pages[i].classes = HIDE_XS;
            for (let i = pages.length - 1; i > idx + 1; i--) pages[i].classes = HIDE_XS;
          }
        }
        return pages;
      },
      buttons() {
        const current = this.page;
        const count = this.numberOfPages;
        const endButton = (key, linkTo, label, text, pageTest) => {
          const disabled = current === pageTest || linkTo < 1 || linkTo > count;
          return {
            key, label, text, disabled,
            page: Math.min(Math.max(linkTo, 1), count),
            role: 'presentation',
            itemRole: 'menuitem',
            tabindex: '-1',
            classes: { disabled },
            ariaHidden: disabled ? 'true' : null
          };
        };
        const ellipsis = key => ({ key, isEllipsis: true, role: 'separator', classes: ['disabled', HIDE_XS] });

        const list = [
          endButton('pagination-goto-first', 1, 'Go to first page', this.firstText, 1),
          endButton('pagination-goto-prev', current - 1, 'Go to previous page', this.prevText, 1)
        ];
        if (this.params.showFirstDots) list.push(ellipsis('ellipsis-first'));
        this.pageList.forEach(page => {
          const active = page.number === current;
          list.push({
            key: `page-${page.number}`,
            page: page.number,
            text: String(page.number),
            label: `Go to page ${page.number}`,
            role: 'presentation',
            itemRole: 'menuitemradio',
            tabindex: active ? '0' : '-1',
            ariaChecked: active ? 'true' : 'false',
            posinset: page.number,
            setsize: count,
            classes: [{ active }, page.classes]
          });
        });
        if (this.params.showLastDots) list.push(ellipsis('ellipsis-last'));
        list.push(
          endButton('pagination-goto-next', current + 1, 'Go to next page', this.nextText, count),
          endButton('pagination-goto-last', count, 'Go to last page', this.lastText, count)
        );
        return list;
      }
    },
    watch: {
      currentPage(value) {
        const page = Math.max(toInt(value, 1), 1);
        if (page !== this.localPage) this.localPage = page;
      }
    },
    methods: {
      onClick(event, pageNumber) {
        const page = Math.min(Math.max(pageNumber, 1), this.numberOfPages);
        if (page === this.page) return;
        const target = event.currentTarget;
        this.localPage = page;
        this.$emit('update:currentPage', page);
        this.$emit('change', page);
        this.$nextTick(() => {
          if (target && this.$el.contains(target) && target.offsetParent !== null) target.focus();
          else this.focusCurrent();
        });
      },
      getButtons() {
        return Array.from(this.$el.querySelectorAll('button.page-link')).filter(btn => btn.offsetParent !== null);
      },
      focusCurrent() {
        const current = this.getButtons().find(btn => toInt(btn.getAttribute('aria-posinset'), 0) === this.page);
        if (current) current.focus();
      },
      /* Sol/yukarı ve sağ/aşağı düğmeler arasında gezer; Shift ile ilk/son (bootstrap-vue). */
      handleKeyNav(event) {
        const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
        const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
        if (!back && !forward) return;
        event.preventDefault();
        const buttons = this.getButtons();
        if (!buttons.length) return;
        const index = buttons.indexOf(document.activeElement);
        let next;
        if (event.shiftKey) next = back ? buttons[0] : buttons[buttons.length - 1];
        else next = buttons[back ? Math.max(0, index - 1) : Math.min(buttons.length - 1, index + 1)];
        if (next) next.focus();
      }
    }
  };
</script>

<style>
  /* bootstrap-vue kuralı (Aşama 5'e kadar bootstrap-vue-parity.css'teydi): dar ekranda ilk/son düğmeleri gizlenir. */
  @media (max-width: 575.98px) {
    .bv-d-xs-down-none {
      display: none !important;
    }
  }
</style>
